/**
 * ============================================================
 * Sparkles.ph — Order Tracking Page
 * File   : assets/tracking.js
 * Version: 4.0 — uses Netlify proxy for 17track API calls
 *                (fixes CORS restriction on browser calls)
 *
 * Config injected by Liquid in page.tracking.liquid:
 *   window.SPARKLES_CONFIG.proxyUrl       — Netlify function URL
 *   window.SPARKLES_CONFIG.storefrontToken — Shopify Storefront token
 *   window.SPARKLES_CONFIG.shopDomain      — myshopify.com domain
 * ============================================================
 */

(function () {
  'use strict';

  /* ── CONFIG ─────────────────────────────────────────────── */
  const CFG = window.SPARKLES_CONFIG || {};

  /* ── 17TRACK STATUS MAP ──────────────────────────────────── */
  const STATUS_MAP = {
    NotFound:                 { label: 'Pending pickup',    step: 0, cls: 'pending'    },
    InfoReceived:             { label: 'Order confirmed',   step: 0, cls: 'processing' },
    InTransit:                { label: 'In transit',        step: 2, cls: 'transit'    },
    InTransit_Other:          { label: 'In transit',        step: 2, cls: 'transit'    },
    AvailableForPickup:       { label: 'Out for delivery',  step: 3, cls: 'transit'    },
    OutForDelivery:           { label: 'Out for delivery',  step: 3, cls: 'transit'    },
    OutForDelivery_Other:     { label: 'Out for delivery',  step: 3, cls: 'transit'    },
    Delivered:                { label: 'Delivered',         step: 4, cls: 'delivered'  },
    Delivered_001:            { label: 'Delivered',         step: 4, cls: 'delivered'  },
    Expired:                  { label: 'Delayed',           step: 2, cls: 'transit'    },
    UndeliverableAsAddressed: { label: 'Failed attempt',    step: 4, cls: 'failed', failed: true },
    AttemptFail:              { label: 'Failed attempt',    step: 4, cls: 'failed', failed: true },
    Exception:                { label: 'Delivery issue',    step: 4, cls: 'failed', failed: true }
  };

  /* ── TIMELINE STEPS ──────────────────────────────────────── */
  const STEPS = [
    { label: 'Order confirmed',  sub: 'Payment received'           },
    { label: 'Picked up by LBC', sub: 'Collected from warehouse'   },
    { label: 'In transit',       sub: 'Parcel moving to your area' },
    { label: 'Out for delivery', sub: 'On the way to you today'    },
    { label: 'Delivered',        sub: 'Signed and received'        }
  ];

  /* ── STATE ───────────────────────────────────────────────── */
  let toastTimer = null;

  /* ── INIT ────────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', function () {

    var input = document.getElementById('sp-track-input');
    if (input) {
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') spDoSearch();
      });
    }

    /* Auto-fill from URL params */
    var params  = new URLSearchParams(window.location.search);
    var autoVal = params.get('tracking')
               || params.get('order')
               || params.get('q')
               || params.get('nums')
               || '';

    if (autoVal && input) {
      input.value = autoVal.trim();
      spDoSearch();
    }
  });

  /* ── PUBLIC: TRIGGER SEARCH ─────────────────────────────── */
  window.spDoSearch = function () {
    var val = (document.getElementById('sp-track-input').value || '').trim();
    if (!val) {
      spToast('Please enter a tracking number, order number, or email.');
      return;
    }
    spSetLoading(true);
    spHideResult();
    spHideEmpty();
    spSearch(val);
  };

  /* ── MAIN SEARCH ROUTER ──────────────────────────────────── */
  async function spSearch(query) {
    try {
      var clean      = query.replace(/\s/g, '');
      var isTracking = /^\d{10,17}$/.test(clean);

      if (isTracking) {
        await spSearchByTracking(clean);
      } else {
        spSetLoading(false);
        spShowEmpty('Please enter your LBC tracking number (10–17 digits). You can find it in your Packed & Ready SMS or email.');
      }

    } catch (err) {
      spSetLoading(false);
      spShowEmpty('Something went wrong. Please try again.');
      console.warn('[Sparkles Tracking]', err.message || err);
    }
  }

  /* ── PATH A: TRACKING NUMBER → NETLIFY PROXY → 17TRACK ──── */
  async function spSearchByTracking(trackingNum) {
    var proxyUrl = CFG.proxyUrl;

    if (!proxyUrl) {
      spSetLoading(false);
      spShowEmpty('Tracking service is not configured. Please contact us for help.');
      return;
    }

    try {
      var res = await fetch(proxyUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        cache: 'no-store',
        body: JSON.stringify({ trackingNumber: trackingNum, _t: Date.now() })
      });

      if (!res.ok) {
        throw new Error('Proxy returned ' + res.status);
      }

      var data      = await res.json();
      var trackInfo = data.trackInfo || null;

      spSetLoading(false);

      var trackingUrl = 'https://www.lbcexpress.com/track/?tracking_number=' + trackingNum;

      var displayOrder = {
        name:         null,
        isTracking:   true,
        lineItems:    { edges: [] },
        fulfillments: [{ trackingInfo: [{ number: trackingNum, url: trackingUrl }] }]
      };

      if (!trackInfo) {
        /* Registered with 17track but LBC hasn't scanned yet —
           show the result panel with a pending status instead of
           an error so it looks intentional rather than broken. */
        var pendingTrackInfo = {
          latest_status: { status: 'InfoReceived', sub_status: '' },
          tracking:      { providers: [] },
          _pendingScan:  true
        };
        spShowResult(displayOrder, trackingNum, trackingUrl, pendingTrackInfo);
        return;
      }

      spShowResult(displayOrder, trackingNum, trackingUrl, trackInfo);

    } catch (err) {
      spSetLoading(false);
      spShowEmpty('Could not retrieve tracking info. Please try again later.');
      console.warn('[Sparkles Tracking] proxy error:', err.message);
    }
  }

  /* ── PATH B + C: SHOPIFY ORDER / EMAIL LOOKUP ────────────── */
  /* Disabled — only LBC tracking number search is active.
     Order/email lookup will be re-enabled once Node.js server is live. */

  /* ── SHOPIFY ORDER LOOKUP ───────────────────────────────────
     NOTE: Storefront API does not support order search by name
     or email. Full order/email lookup is handled by the Node.js
     server endpoint /lookup once it is deployed.
  ── */

  /* ── RENDER RESULT ───────────────────────────────────────── */
  function spShowResult(order, trackingNum, trackingUrl, trackInfo) {
    var panel = document.getElementById('sp-result-panel');
    var empty = document.getElementById('sp-empty-state');
    if (panel) panel.style.display = 'block';
    if (empty) empty.style.display = 'none';

    /* Order number row */
    var orderRow = document.getElementById('sp-r-order-row');
    if (order.name && !order.isTracking) {
      spSet('sp-r-order-num', order.name);
      if (orderRow) orderRow.style.display = '';
    } else {
      if (orderRow) orderRow.style.display = 'none';
    }

    /* Item summary */
    var itemCount = 0;
    if (order.lineItems && order.lineItems.edges) {
      order.lineItems.edges.forEach(function (e) {
        itemCount += (e.node && e.node.quantity) ? e.node.quantity : 1;
      });
    }
    spSet('sp-r-customer', itemCount > 0
      ? itemCount + (itemCount === 1 ? ' item' : ' items')
      : '18k Gold Jewelry'
    );

    /* Tracking number */
    spSet('sp-r-tracking-num', trackingNum || 'Not yet assigned');

    /* LBC direct link */
    var lbcWrap = document.getElementById('sp-track-lbc');
    var lbcLink = document.getElementById('sp-lbc-link');
    if (trackingNum && lbcWrap && lbcLink) {
      lbcLink.href = trackingUrl ||
        'https://www.lbcexpress.com/track/?tracking_number=' + trackingNum;
      lbcWrap.style.display = 'block';
    } else if (lbcWrap) {
      lbcWrap.style.display = 'none';
    }

    /* Resolve status */
    var rawStatus = (trackInfo && trackInfo.latest_status && trackInfo.latest_status.status)     || 'NotFound';
    var subStatus = (trackInfo && trackInfo.latest_status && trackInfo.latest_status.sub_status) || '';
    var mapped    = STATUS_MAP[subStatus] || STATUS_MAP[rawStatus] || STATUS_MAP['NotFound'];

    if (!trackingNum) {
      mapped = { label: 'Processing', step: 0, cls: 'processing' };
    }

    /* Status badge */
    var badge = document.getElementById('sp-r-status-badge');
    if (badge) {
      badge.className   = 'sp-badge sp-badge--' + mapped.cls;
      badge.textContent = mapped.label;
    }

    /* Scan events */
    var events = [];
    if (
      trackInfo &&
      trackInfo.tracking &&
      trackInfo.tracking.providers &&
      trackInfo.tracking.providers[0] &&
      trackInfo.tracking.providers[0].events
    ) {
      events = trackInfo.tracking.providers[0].events.slice().reverse();
    }

    /* Build timeline */
    var tl = document.getElementById('sp-r-timeline');
    if (tl) {
      tl.innerHTML = STEPS.map(function (s, i) {
        var isDone   = i < mapped.step;
        var isActive = i === mapped.step;
        var isPend   = i > mapped.step;

        var eventData = events[i] || null;
        var subText   = eventData
          ? eventData.description
          : (isActive || isDone ? s.sub : '');
        var timeText  = eventData ? spFormatTime(eventData.time_iso) : '';

        var stepLabel = s.label;
        if (i === 4 && mapped.failed) stepLabel = 'Failed delivery attempt';

        var dotCls   = isDone   ? 'sp-tl-dot--done'   : (isActive ? 'sp-tl-dot--active' : '');
        var titleCls = isPend   ? 'sp-tl-title--muted' : '';
        var subCls   = isPend   ? 'sp-tl-sub--muted'   : '';

        return [
          '<div class="sp-tl-step">',
          '<div class="sp-tl-dot '   + dotCls   + '"></div>',
          '<div class="sp-tl-title ' + titleCls + '">' + spEsc(stepLabel) + '</div>',
          subText  ? '<div class="sp-tl-sub '  + subCls + '">' + spEsc(subText)  + '</div>' : '',
          timeText ? '<div class="sp-tl-time">'            + spEsc(timeText) + '</div>' : '',
          '</div>'
        ].join('');
      }).join('');
    }

    /* Recent scan events list */
    var scanList = document.getElementById('sp-r-scan-list');
    if (scanList) {
      if (events.length > 0) {
        scanList.style.display = 'block';
        var recent = events.slice(-5).reverse();
        scanList.innerHTML = recent.map(function (ev) {
          return [
            '<div class="sp-scan-event">',
            '<div class="sp-scan-time">' + spEsc(spFormatTime(ev.time_iso)) + '</div>',
            '<div class="sp-scan-desc">' + spEsc(ev.description || '')      + '</div>',
            '<div class="sp-scan-loc">'  + spEsc(ev.location    || '')      + '</div>',
            '</div>'
          ].join('');
        }).join('');
      } else if (trackInfo && trackInfo._pendingScan) {
        /* Registered but not yet scanned by LBC — show helpful notice */
        scanList.style.display = 'block';
        scanList.innerHTML = [
          '<div class="sp-scan-event">',
          '<div class="sp-scan-desc">Your parcel has been registered and handed to LBC. ',
          'Tracking updates will appear here once LBC scans your parcel at their facility. ',
          'This usually takes a few hours after pickup.</div>',
          '</div>'
        ].join('');
      } else {
        scanList.style.display = 'none';
      }
    }
  }

  /* ── EMPTY STATE ─────────────────────────────────────────── */
  function spShowEmpty(msg) {
    var panel = document.getElementById('sp-result-panel');
    var empty = document.getElementById('sp-empty-state');
    var emsg  = document.getElementById('sp-empty-msg');
    if (panel) panel.style.display = 'none';
    if (empty) empty.style.display = 'block';
    if (emsg && msg) emsg.textContent = msg;
  }

  function spHideEmpty() {
    var empty = document.getElementById('sp-empty-state');
    if (empty) empty.style.display = 'none';
  }

  function spHideResult() {
    var panel = document.getElementById('sp-result-panel');
    if (panel) panel.style.display = 'none';
  }

  /* ── PUBLIC: TAB SWITCHER ────────────────────────────────── */
  window.spSwitchTab = function (el, type) {
    var input = document.getElementById('sp-track-input');
    if (input) {
      input.placeholder = 'e.g. 118800000025971';
      input.value = '';
      input.focus();
    }
    spHideResult();
    spHideEmpty();
  };

  /* ── PUBLIC: COPY TRACKING NUMBER ───────────────────────── */
  window.spCopyTracking = function () {
    var el  = document.getElementById('sp-r-tracking-num');
    var num = el ? el.textContent.trim() : '';
    if (!num || num === 'Not yet assigned') {
      spToast('No tracking number to copy yet.');
      return;
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(num)
        .then(function () { spToast('Tracking number copied!'); })
        .catch(function () { spFallbackCopy(num); });
    } else {
      spFallbackCopy(num);
    }
  };

  function spFallbackCopy(text) {
    var ta = document.createElement('textarea');
    ta.value = text;
    ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;';
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand('copy');
      spToast('Tracking number copied!');
    } catch (e) {
      spToast('Tracking: ' + text);
    }
    document.body.removeChild(ta);
  }

  /* ── HELPERS ─────────────────────────────────────────────── */
  function spSetLoading(on) {
    var bar = document.getElementById('sp-loading-bar');
    var btn = document.getElementById('sp-track-btn');
    if (bar) bar.classList.toggle('sp-loading-bar--active', on);
    if (btn) {
      btn.disabled    = on;
      btn.textContent = on ? 'Searching\u2026' : 'Track';
    }
  }

  function spSet(id, text) {
    var el = document.getElementById(id);
    if (el) el.textContent = text;
  }

  function spEsc(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g,  '&amp;')
      .replace(/</g,  '&lt;')
      .replace(/>/g,  '&gt;')
      .replace(/"/g,  '&quot;');
  }

  function spFormatTime(iso) {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('en-PH', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch (e) { return iso; }
  }

  function spToast(msg) {
    var t = document.getElementById('sp-toast');
    if (!t) return;
    t.textContent = msg;
    t.classList.add('sp-toast--show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      t.classList.remove('sp-toast--show');
    }, 2800);
  }

})();
